/**
 * Five-field cron (minute hour day-of-month month day-of-week) for the kit's
 * schedule editor: building and recognising the common shapes, validating a
 * custom expression, a German one-line summary and the next run times —
 * computed locally in the browser's time zone.
 */

export type NxCronFrequency =
  | 'every_n_minutes'
  | 'hourly'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly'
  | 'custom';

/** A schedule in its structured form; only the fields its `frequency` needs count. */
export interface NxCronSchedule {
  frequency: NxCronFrequency;
  /** 0–59 */
  minute: number;
  /** 0–23 */
  hour: number;
  /** Interval for `every_n_minutes`: 5, 10, 15 or 30. */
  everyN: number;
  /** 0 = Sunday … 6 = Saturday (cron day of week). */
  weekday: number;
  /** 1–31 */
  dayOfMonth: number;
}

export const NX_CRON_DEFAULT: NxCronSchedule = {
  frequency: 'daily',
  minute: 0,
  hour: 8,
  everyN: 15,
  weekday: 1,
  dayOfMonth: 1,
};

export const NX_CRON_EVERY_N = [5, 10, 15, 30] as const;

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, Math.trunc(n)));
}

export function pad2(n: number): string {
  return String(clamp(n, 0, 99)).padStart(2, '0');
}

/** The cron expression of a structured schedule. */
export function buildCron(s: NxCronSchedule): string {
  const m = clamp(s.minute, 0, 59);
  const h = clamp(s.hour, 0, 23);
  const n = (NX_CRON_EVERY_N as readonly number[]).includes(s.everyN) ? s.everyN : 15;
  const dow = clamp(s.weekday, 0, 6);
  const dom = clamp(s.dayOfMonth, 1, 31);
  switch (s.frequency) {
    case 'every_n_minutes':
      return `*/${n} * * * *`;
    case 'hourly':
      return `${m} * * * *`;
    case 'weekdays':
      return `${m} ${h} * * 1-5`;
    case 'weekly':
      return `${m} ${h} * * ${dow}`;
    case 'monthly':
      return `${m} ${h} ${dom} * *`;
    case 'daily':
    case 'custom':
    default:
      return `${m} ${h} * * *`;
  }
}

/** The structured form of an expression of a known shape, `null` for anything custom. */
export function parseCron(expr: string): NxCronSchedule | null {
  const raw = expr.trim().replace(/^CRON_TZ=\S+\s+/, '');
  const parts = raw.split(/\s+/);
  if (!raw || parts.length !== 5) return null;
  // Only a valid expression gets a structured form — `60 * * * *` must stay a
  // custom (and flagged) expression, never be clamped into a different plan.
  if (validateCron(raw)) return null;
  const [min, hour, dom, mon, dow] = parts as [string, string, string, string, string];
  if (mon !== '*') return null;

  const step = min.match(/^\*\/(\d+)$/);
  if (step && hour === '*' && dom === '*' && dow === '*') {
    const n = Number(step[1]);
    return (NX_CRON_EVERY_N as readonly number[]).includes(n)
      ? { ...NX_CRON_DEFAULT, frequency: 'every_n_minutes', everyN: n, minute: 0, hour: 0 }
      : null;
  }
  if (/^\d+$/.test(min) && hour === '*' && dom === '*' && dow === '*') {
    return { ...NX_CRON_DEFAULT, frequency: 'hourly', minute: clamp(Number(min), 0, 59), hour: 0 };
  }
  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) return null;
  const minute = clamp(Number(min), 0, 59);
  const h = clamp(Number(hour), 0, 23);
  if (dom === '*' && dow === '*')
    return { ...NX_CRON_DEFAULT, frequency: 'daily', minute, hour: h };
  if (dom === '*' && dow === '1-5') {
    return { ...NX_CRON_DEFAULT, frequency: 'weekdays', minute, hour: h };
  }
  if (dom === '*' && /^\d+$/.test(dow)) {
    const weekday = Number(dow) === 7 ? 0 : clamp(Number(dow), 0, 6);
    return { ...NX_CRON_DEFAULT, frequency: 'weekly', minute, hour: h, weekday };
  }
  if (/^\d+$/.test(dom) && dow === '*') {
    return {
      ...NX_CRON_DEFAULT,
      frequency: 'monthly',
      minute,
      hour: h,
      dayOfMonth: clamp(Number(dom), 1, 31),
    };
  }
  return null;
}

const FIELDS = [
  { name: 'Minute', min: 0, max: 59 },
  { name: 'Stunde', min: 0, max: 23 },
  { name: 'Tag', min: 1, max: 31 },
  { name: 'Monat', min: 1, max: 12 },
  { name: 'Wochentag', min: 0, max: 7 },
] as const;

/** The set of values a cron field allows, or an error text. */
function expandField(part: string, min: number, max: number): Set<number> | string {
  const out = new Set<number>();
  for (const item of part.split(',')) {
    const m = item.match(/^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/);
    if (!m) return `„${item}“ ist kein gültiger Wert`;
    let lo = min;
    let hi = max;
    if (m[1] !== '*') {
      const [a, b] = m[1]!.split('-').map(Number) as [number, number | undefined];
      lo = a;
      hi = b ?? (m[2] ? max : a);
    }
    const step = m[2] ? Number(m[2]) : 1;
    if (lo < min || hi > max || lo > hi) return `„${item}“ liegt außerhalb ${min}–${max}`;
    if (step < 1) return `Schrittweite in „${item}“ muss mindestens 1 sein`;
    for (let v = lo; v <= hi; v += step) out.add(v);
  }
  return out;
}

interface ParsedCron {
  minutes: Set<number>;
  hours: Set<number>;
  days: Set<number>;
  months: Set<number>;
  weekdays: Set<number>;
  daysRestricted: boolean;
  weekdaysRestricted: boolean;
}

function parseFields(expr: string): ParsedCron | string {
  const raw = expr.trim().replace(/^CRON_TZ=\S+\s+/, '');
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length !== 5)
    return 'Ein Ausdruck braucht genau fünf Felder: Minute Stunde Tag Monat Wochentag';
  const sets: Set<number>[] = [];
  for (let i = 0; i < 5; i++) {
    const f = FIELDS[i]!;
    const res = expandField(parts[i]!, f.min, f.max);
    if (typeof res === 'string') return `${f.name}: ${res}`;
    sets.push(res);
  }
  const weekdays = sets[4]!;
  if (weekdays.has(7)) {
    weekdays.delete(7);
    weekdays.add(0);
  }
  return {
    minutes: sets[0]!,
    hours: sets[1]!,
    days: sets[2]!,
    months: sets[3]!,
    weekdays,
    daysRestricted: parts[2] !== '*',
    weekdaysRestricted: parts[4] !== '*',
  };
}

/** Why an expression is invalid (German), `null` when it is fine. */
export function validateCron(expr: string): string | null {
  const res = parseFields(expr);
  return typeof res === 'string' ? res : null;
}

/**
 * The next `count` run times after `from`, in local time. Day-of-month and
 * day-of-week combine as in classic cron: when both are restricted, either
 * matches. Searches at most a year ahead; an invalid expression gives `[]`.
 */
export function nextCronRuns(expr: string, from: Date = new Date(), count = 3): Date[] {
  const c = parseFields(expr);
  if (typeof c === 'string') return [];
  const out: Date[] = [];
  const MINUTE = 60_000;
  // Minutes and hours advance in absolute time and are only READ in local
  // time: local arithmetic (setHours/setMinutes) jumps over the repeated hour
  // when the clocks go back and would miss runs in it.
  let t = new Date(Math.floor(from.getTime() / MINUTE) * MINUTE + MINUTE);
  const limit = from.getTime() + 366 * 24 * 60 * 60 * 1000;
  const dayOk = (d: Date) => {
    const dom = c.days.has(d.getDate());
    const dow = c.weekdays.has(d.getDay());
    if (c.daysRestricted && c.weekdaysRestricted) return dom || dow;
    if (c.daysRestricted) return dom;
    if (c.weekdaysRestricted) return dow;
    return true;
  };
  while (out.length < count && t.getTime() <= limit) {
    if (!c.months.has(t.getMonth() + 1)) {
      t.setMonth(t.getMonth() + 1, 1);
      t.setHours(0, 0, 0, 0);
      continue;
    }
    if (!dayOk(t)) {
      t.setDate(t.getDate() + 1);
      t.setHours(0, 0, 0, 0);
      continue;
    }
    if (!c.hours.has(t.getHours())) {
      t = new Date(t.getTime() + (60 - t.getMinutes()) * MINUTE);
      continue;
    }
    if (!c.minutes.has(t.getMinutes())) {
      t = new Date(t.getTime() + MINUTE);
      continue;
    }
    out.push(new Date(t.getTime()));
    t = new Date(t.getTime() + MINUTE);
  }
  return out;
}

/** German weekday names, cron order (0 = Sunday). */
export const NX_CRON_WEEKDAYS = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
] as const;

/**
 * One German line for an expression: „Jeden Montag um 08:00“. A custom
 * expression reads „Eigener Zeitplan: 0 8 * * 1-5“; an invalid one returns ''.
 */
export function describeCron(
  expr: string,
  weekdayNames: readonly string[] = NX_CRON_WEEKDAYS
): string {
  if (validateCron(expr)) return '';
  const s = parseCron(expr);
  if (!s) return `Eigener Zeitplan: ${expr.trim()}`;
  const time = `${pad2(s.hour)}:${pad2(s.minute)}`;
  switch (s.frequency) {
    case 'every_n_minutes':
      return `Alle ${s.everyN} Minuten`;
    case 'hourly':
      return `Stündlich zur Minute ${pad2(s.minute)}`;
    case 'weekdays':
      return `Werktags (Mo–Fr) um ${time}`;
    case 'weekly':
      return `Jeden ${weekdayNames[s.weekday] ?? ''} um ${time}`;
    case 'monthly':
      return `Monatlich am ${s.dayOfMonth}. um ${time}`;
    case 'daily':
    default:
      return `Täglich um ${time}`;
  }
}
